import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import { COLORS } from "./constants";
import { inputSx, barePanelSx, dangerButtonSx, primaryButtonSx, secondaryButtonSx, sectionSubtleSx, sectionTitleSx, statusPillSx } from "./styles";

// VP9 FIRST. The old order put VP8 first, so Chrome always chose it — VP8 is
// the oldest and weakest of the three and needs roughly 30-50% more bitrate for
// the same picture. H.264 leads where a browser offers it (Safari, some Chrome
// builds): the server re-encodes to H.264 anyway, so starting there avoids one
// generation of transcoding loss.
const RECORDER_MIME_CANDIDATES = [
  "video/webm;codecs=h264,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm"
];

function pickSupportedMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  return RECORDER_MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || null;
}

// `{ video: true }` lets the browser pick, and browsers pick small — commonly
// 640x480. That is the black bars either side of the preview (a 4:3 picture in a
// 16:9 frame) and most of the reason the result looked soft.
//
// `ideal` rather than `exact` on purpose: a webcam that cannot do 1080p gets the
// closest it can rather than failing outright, and a phone picks a sensor mode
// near this instead of its lowest.
const CAMERA_CONSTRAINTS = {
  width:     { ideal: 1920 },
  height:    { ideal: 1080 },
  frameRate: { ideal: 30, max: 60 }
};

const SCREEN_CONSTRAINTS = {
  width:     { ideal: 1920 },
  height:    { ideal: 1080 },
  frameRate: { ideal: 30, max: 60 }
};

// What the platforms actually want for each size, rather than whatever
// MediaRecorder would default to. Keyed off the height the camera really gave
// us, so a 720p webcam is not asked to carry a 1080p bitrate it cannot fill and
// a 1080p one is not starved.
// ── Choosing a lens on a phone ───────────────────────────────────────────────
//
// A modern phone exposes each rear lens as its OWN device: main, ultra-wide,
// telephoto. Asking for 1920x1080 without naming one lets the browser answer
// with whichever it likes, and several of them answer with the ULTRA-WIDE —
// which is why the picture came out distorted and far too wide.
//
// Resolution constraints cannot fix this: an ultra-wide shoots 1080p perfectly
// well. The field of view is a property of the lens, so the lens has to be
// picked by device id.
//
// Labels are not standardised. iOS says "Back Camera", "Back Ultra Wide
// Camera", "Back Telephoto Camera"; Android tends toward "camera2 0, facing
// back". So this scores rather than pattern-matches, and the user can always
// override it with the picker.
function scoreCamera(label = "", index = 0) {
  const l = label.toLowerCase();
  let score = 0;

  // The lenses to avoid as a default. Ultra-wide is the one that causes this
  // complaint; telephoto is the opposite problem but equally not what someone
  // expects when they press "Use Camera".
  if (l.includes("ultra")) score -= 100;
  if (l.includes("telephoto") || l.includes("tele")) score -= 60;

  // "Back Camera" on iOS and the plain back camera on Android are the main one.
  if (l.includes("back") || l.includes("rear") || l.includes("environment")) score += 20;
  // A plain string test rather than a regex. Written as a word-boundary
  // pattern it passed through a layer of shell and Python escaping that
  // turned each boundary into a literal backspace character, so the regex
  // matched nothing and this bonus never applied.
  if (l.includes("back camera")) score += 25;

  // A front camera is a perfectly good default on a laptop, just not the first
  // choice on a phone that also has a back one.
  if (l.includes("front") || l.includes("user") || l.includes("face")) score += 5;

  // Where labels say nothing useful (Android often just numbers them), the
  // lowest index is conventionally the main sensor.
  score -= index;
  return score;
}

function pickDefaultCamera(devices) {
  if (!devices.length) return "";
  const best = devices
    .map((d, i) => ({ id: d.deviceId, score: scoreCamera(d.label, i) }))
    .sort((a, b) => b.score - a.score)[0];
  return best?.id || "";
}

function targetVideoBitrate(height) {
  if (height >= 1080) return 4_500_000;
  if (height >= 720)  return 2_500_000;
  if (height >= 480)  return 1_200_000;
  return 800_000;
}

// `previewHost` is a DOM node in the preview frame at the top of the page. When
// it is present the camera surface is PORTALLED there instead of being drawn in
// this panel, so the page shows one preview instead of two black boxes.
//
// A portal and not a move: the <video>, its ref, the MediaStream and every
// effect stay inside this component's React tree, so nothing about a live
// capture is disturbed by where the pixels end up.
export function BrowserEncodePanel({ showMessage, previewHost = null }) {
  const [mode,   setMode]   = useState(null);   // "camera" | "screen" | null
  const [status, setStatus] = useState("idle"); // idle | previewing | connecting | live | error

  // Available video inputs, and which one is in use. Populated only AFTER
  // permission is granted — before that the browser returns devices with empty
  // labels, so there is nothing to choose between.
  const [cameras,  setCameras]  = useState([]);
  const [cameraId, setCameraId] = useState("");

  // Whether the microphone is muted for VIEWERS. Kept in a ref as well as state
  // because every newly opened stream has to inherit it, and those opens happen
  // outside React's render cycle (switching lens, restarting capture) where the
  // state value would be stale.
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);

  // Screen shares often carry no audio — the person can decline to share it in
  // the browser's own picker — so the control explains why it is unavailable
  // rather than looking broken.
  //
  // State, not a read off localStreamRef: a ref does not re-render, so after
  // switching lens the button would still be judging the PREVIOUS stream.
  const [hasAudio, setHasAudio] = useState(false);

  const videoRef        = useRef(null);
  const localStreamRef   = useRef(null);
  const wsRef            = useRef(null);
  const recorderRef      = useRef(null);

  const stopAll = () => {
    try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch {}
    recorderRef.current = null;

    try { wsRef.current?.close(1000, "user stopped"); } catch {}
    wsRef.current = null;

    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setHasAudio(false);

    setMode(null);
    setStatus("idle");
  };

  useEffect(() => () => stopAll(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // The <video> element only mounts once `mode` is set (conditional render
  // below), so attaching srcObject has to happen in an effect that runs
  // AFTER that mount — doing it inline in startCapture() was a no-op because
  // videoRef.current was still null at that point (video preview stayed black).
  // `previewHost` belongs in these deps as much as `mode` does. Portalling the
  // video moves it to a DIFFERENT DOM node, so React unmounts the old <video>
  // and mounts a new one — videoRef.current becomes a fresh element with no
  // srcObject. Without this dependency the effect would not re-run (mode did
  // not change) and the preview would go black exactly as it did before the
  // effect was introduced.
  useEffect(() => {
    if (mode && videoRef.current && localStreamRef.current) {
      videoRef.current.srcObject = localStreamRef.current;
    }
  }, [mode, previewHost]);

  const AUDIO_CONSTRAINTS = {
    // Echo cancellation and noise suppression are on by default for a camera;
    // they are tuned for a phone call and audibly hurt music and anything else
    // being broadcast.
    echoCancellation: { ideal: false },
    noiseSuppression: { ideal: false },
    autoGainControl:  { ideal: false },
    channelCount:     { ideal: 2 },
    sampleRate:       { ideal: 48000 }
  };

  // Device labels are blank until permission has been granted once, so this is
  // always called AFTER a successful getUserMedia rather than before.
  const refreshCameras = async (activeDeviceId) => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const inputs = all.filter((d) => d.kind === "videoinput");
      setCameras(inputs);
      if (activeDeviceId) setCameraId(activeDeviceId);
      else if (!cameraId) setCameraId(pickDefaultCamera(inputs));
      return inputs;
    } catch { return []; }
  };

  // Muting is `track.enabled = false`, NOT stopping or removing the track.
  // A disabled track keeps producing frames, they are simply silent — so the
  // MediaRecorder and the WebSocket carry on undisturbed and the broadcast
  // survives. Stopping the track instead would end the audio stream and take
  // the encoder down with it mid-broadcast.
  const applyMute = (stream) => {
    const tracks = stream?.getAudioTracks?.() || [];
    tracks.forEach((t) => { t.enabled = !mutedRef.current; });
    setHasAudio(tracks.length > 0);
  };

  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    applyMute(localStreamRef.current);
  };

  const openCamera = async (deviceId) => {
    // `exact` once a specific lens has been chosen: `ideal` would let the
    // browser quietly fall back to the ultra-wide again, which is the whole
    // problem being solved here.
    const video = deviceId
      ? { ...CAMERA_CONSTRAINTS, deviceId: { exact: deviceId } }
      : { ...CAMERA_CONSTRAINTS, facingMode: { ideal: "environment" } };

    return navigator.mediaDevices.getUserMedia({ video, audio: AUDIO_CONSTRAINTS });
  };

  const startCapture = async (kind) => {
    try {
      let stream;

      if (kind === "screen") {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: SCREEN_CONSTRAINTS, audio: true
        });
      } else {
        // First open may have no chosen lens yet. Open once to obtain
        // permission, read the list, and if the browser handed back something
        // other than the lens we would have picked, reopen on the right one.
        stream = await openCamera(cameraId || "");
        const settings = stream.getVideoTracks()[0]?.getSettings?.() || {};
        const inputs   = await refreshCameras(settings.deviceId);

        if (!cameraId && inputs.length > 1) {
          const preferred = pickDefaultCamera(inputs);
          if (preferred && preferred !== settings.deviceId) {
            stream.getTracks().forEach((t) => t.stop());
            stream = await openCamera(preferred);
            setCameraId(preferred);
          }
        }
      }

      // A stream opened after the user muted must start muted too, or switching
      // source would silently put them back on air.
      applyMute(stream);

      localStreamRef.current = stream;
      setMode(kind);
      setStatus("previewing");

      // If the user stops sharing from the browser's own "Stop sharing" UI
      stream.getVideoTracks()[0]?.addEventListener("ended", stopAll);
    } catch (err) {
      showMessage?.(err.message || "Could not access camera/screen", "error");
    }
  };

  /** Swap lens without dropping the preview. Disabled once live. */
  const switchCamera = async (deviceId) => {
    setCameraId(deviceId);
    if (mode !== "camera" || status === "live" || status === "connecting") return;

    try {
      const next = await openCamera(deviceId);
      // Only tear the old one down once the new one is actually open — if the
      // lens is busy or refuses the constraints, the preview survives.
      applyMute(next);
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = next;
      if (videoRef.current) videoRef.current.srcObject = next;
      next.getVideoTracks()[0]?.addEventListener("ended", stopAll);
    } catch (err) {
      showMessage?.(err.message || "Could not switch camera", "error");
    }
  };

  const goLive = () => {
    if (!localStreamRef.current) return;
    const mimeType = pickSupportedMimeType();
    if (!mimeType) {
      showMessage?.("This browser can't encode video (MediaRecorder unsupported)", "error");
      return;
    }

    const token = localStorage.getItem("token");
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${wsProtocol}//${window.location.host}/browser-encode?token=${encodeURIComponent(token || "")}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    setStatus("connecting");

    ws.onopen = () => {
      // Without an explicit bitrate MediaRecorder picks a conservative default
      // and the result is soft however good the camera is. The target is chosen
      // from the resolution the camera ACTUALLY gave us — getSettings() reports
      // what was negotiated, which is not necessarily what was asked for.
      const track    = localStreamRef.current.getVideoTracks()[0];
      const settings = track?.getSettings?.() || {};
      const height   = settings.height || 720;

      const recorder = new MediaRecorder(localStreamRef.current, {
        mimeType,
        videoBitsPerSecond: targetVideoBitrate(height),
        audioBitsPerSecond: 128_000
      });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data);
      };
      recorder.start(250); // send a chunk every 250ms
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "started") setStatus("live");
        if (msg.type === "error") { showMessage?.(msg.message || "Stream error", "error"); stopAll(); }
        if (msg.type === "stopped") setStatus("previewing");
      } catch {}
    };

    ws.onerror = () => {
      showMessage?.("Connection to encoder failed", "error");
    };

    ws.onclose = (e) => {
      // Runs on both a clean stop and a failed/dropped connection — either
      // way the encoder is no longer running, so don't leave the UI stuck
      // showing "CONNECTING…" forever.
      try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch {}
      recorderRef.current = null;
      wsRef.current = null;
      if (e.code !== 1000) {
        showMessage?.(`Encoder connection closed (code ${e.code}${e.reason ? `: ${e.reason}` : ""})`, "error");
      }
      setStatus((s) => (s === "connecting" || s === "live") ? "previewing" : s);
    };
  };

  const stopLive = () => {
    try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch {}
    recorderRef.current = null;
    try { wsRef.current?.close(1000, "user stopped"); } catch {}
    wsRef.current = null;
    setStatus("previewing");
  };

  const isLive = status === "live" || status === "connecting";



  // Drawn either here or in the frame at the top — same element either way.
  const previewSurface = mode ? (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <video ref={videoRef} muted autoPlay playsInline
        style={{ width: "100%", height: "100%", objectFit: "contain", background: "#0B0A1F" }} />
      {/* The warning stays for the state that actually costs something: live and
          silent. The preview is deliberately muted, so there is no audible clue
          that viewers are hearing nothing. */}
      {muted && hasAudio && isLive && (
        <Box sx={{
          position: "absolute", top: 10, left: 10,
          display: "flex", alignItems: "center", gap: 0.75,
          px: 1.25, py: 0.5, borderRadius: "999px",
          bgcolor: "rgba(229,72,77,0.92)", color: "#fff",
          fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em"
        }}>
          <MicOffIcon sx={{ fontSize: 14 }} />
          MUTED — VIEWERS HEAR NOTHING
        </Box>
      )}

      {/* Mute lives ON the picture, in the frame at the top of the page where
          the eye already is, rather than in the control row further down.
          Always visible rather than revealed on hover: hover does not exist on
          a phone, and this is the one control someone reaches for in a hurry. */}
      {hasAudio && (
        <Tooltip title={muted ? "Unmute — viewers will hear you again"
                              : "Mute — viewers stop hearing you, the broadcast continues"}>
          <IconButton
            onClick={toggleMute}
            aria-label={muted ? "Unmute microphone" : "Mute microphone"}
            aria-pressed={muted}
            sx={{
              position: "absolute", bottom: 10, right: 10,
              width: 44, height: 44,
              color: "#fff",
              bgcolor: muted ? "rgba(229,72,77,0.92)" : "rgba(0,0,0,0.55)",
              border: `1px solid ${muted ? COLORS.red : "rgba(255,255,255,0.18)"}`,
              backdropFilter: "blur(4px)",
              "&:hover": {
                bgcolor: muted ? COLORS.red : "rgba(0,0,0,0.75)"
              }
            }}
          >
            {muted ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ) : (
    <Typography sx={{ color: COLORS.muted, fontSize: 13, textAlign: "center", px: 2 }}>
      Choose camera or screen below to see a preview
    </Typography>
  );

  return (
    <Box sx={barePanelSx}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
        alignItems={{ sm: "center" }} justifyContent="space-between" mb={2}>
        <Box>
          <Typography sx={sectionTitleSx}>Stream from this device</Typography>
          <Typography sx={sectionSubtleSx}>
            Use your camera or share your screen — no OBS needed, goes straight to your destinations above.
          </Typography>
        </Box>
        <Chip
          icon={<CircleIcon sx={{
            fontSize: "8px !important",
            color: `${status === "live" ? COLORS.green : COLORS.muted} !important`,
            animation: status === "live" ? "pulse 1.4s infinite" : "none"
          }} />}
          label={status === "live" ? "LIVE" : status === "connecting" ? "CONNECTING…" : "OFFLINE"}
          sx={statusPillSx}
        />
      </Stack>

      {previewHost
        ? createPortal(previewSurface, previewHost)
        : (
          <Box sx={{
            position: "relative", aspectRatio: "16/9", bgcolor: "#0B0A1F",
            border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center", mb: 2
          }}>
            {previewSurface}
          </Box>
        )}

      {/* Lens picker. Only worth showing when there is more than one to choose
          from — a laptop with a single webcam gets nothing. Locked while live,
          because swapping the track underneath a running MediaRecorder ends the
          broadcast. */}
      {mode === "camera" && cameras.length > 1 && (
        <TextField
          select
          size="small"
          fullWidth
          value={cameraId || ""}
          onChange={(e) => switchCamera(e.target.value)}
          disabled={isLive}
          label="Camera"
          helperText={
            isLive
              ? "Stop the broadcast to switch camera"
              : "Phones list each lens separately — pick the main one if the picture looks too wide"
          }
          sx={{ ...inputSx, mb: 2 }}
          InputLabelProps={{ sx: { color: COLORS.muted, fontSize: 13 } }}
          FormHelperTextProps={{ sx: { color: COLORS.muted, fontSize: 11, ml: 0.5 } }}
        >
          {cameras.map((cam, i) => (
            <MenuItem key={cam.deviceId || i} value={cam.deviceId}>
              {cam.label || `Camera ${i + 1}`}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        {/* Mute is not here: it sits on the preview picture itself, in the
            frame at the top. See previewSurface above. */}
        {!mode && (
          <>
            <Button startIcon={<VideocamIcon />} variant="outlined" sx={secondaryButtonSx}
              onClick={() => startCapture("camera")}>
              Use Camera
            </Button>
            <Button startIcon={<DesktopWindowsIcon />} variant="outlined" sx={secondaryButtonSx}
              onClick={() => startCapture("screen")}>
              Share Screen
            </Button>
          </>
        )}
        {mode && status === "previewing" && (
          <>
            <Button variant="contained" sx={primaryButtonSx} onClick={goLive}>
              Go Live
            </Button>
            <Button variant="outlined" sx={secondaryButtonSx} onClick={stopAll}>
              Cancel
            </Button>
          </>
        )}
        {mode && isLive && (
          <Button startIcon={<StopCircleIcon />} variant="contained"
            sx={dangerButtonSx}
            onClick={stopLive}>
            Stop Streaming
          </Button>
        )}
      </Stack>
    </Box>
  );
}
